/**
 * Per-contract change table — "Abu Sultan 2.0" (Tools → tab option-change-tracker).
 *
 * One row per option contract: the latest chain snapshot ("now") next to the same contract
 * at the prior session's close, for OI, volume, Δ Γ Θ ν, IV and mid. The join is done on the
 * server (/api/edge/contract-changes); this view picks the underlying and expiries, toggles
 * columns, sorts, and draws one chosen metric prior vs now across strikes.
 *
 * Product rules: null renders "—", never 0; Bid red, Ask green; no vendor names in copy.
 */
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import {
    CartesianGrid,
    Legend,
    Line,
    LineChart,
    ReferenceLine,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";
import { LanguageContext } from "../../contexts/LanguageContext";
import { useChartTheme } from "../../theme/chartTheme";
import {
    ChangeMetric,
    ContractChangeRow,
    ContractChangesError,
    ContractChangesResponse,
    CHANGE_METRICS,
    fetchChangeExpiries,
    fetchContractChanges,
    MAX_EXPIRIES,
    normaliseSymbol,
    Num,
} from "../../services/contractChangesService";
import "./EdgeScanner.css";
import "./ContractChanges.css";
import "./ContractChanges.ds.css";
import { isDesignSystemOn } from "../../theme/mmecDsPreview";
import ToolState from "../mmec/ToolState";

// ── Copy ─────────────────────────────────────────────────────────────────────

type Lang = "en" | "ar";

const COPY = {
    en: {
        eyebrow: "Tools",
        title: "Per-contract change table — Abu Sultan 2.0",
        idea: "One row per contract: OI, volume, greeks (Δ Γ Θ ν), IV and mid now against the prior-session close. Toggle columns and use the chart below (e.g. Γ prior vs now).",
        caption: "An automatic reading of data, not a recommendation",
        underlying: "Underlying",
        side: "Side",
        all: "All",
        calls: "Calls",
        puts: "Puts",
        expiries: `Expiries (up to ${MAX_EXPIRIES})`,
        window: "Strikes",
        windowAll: "All strikes",
        nearSpot: (p: number) => `±${p}% of spot`,
        rankBy: "Rank by",
        rows: "Rows",
        columns: "Columns",
        showPct: "Show %",
        quote: "Bid / Ask",
        refresh: "Refresh",
        loadingExp: "Reading the expiries…",
        loading: "Reading the chain and the prior-session close…",
        pickExpiry: "Pick at least one specific expiry above. Each expiry is compared with its own prior-session close.",
        noExp: "No expiries found for this underlying.",
        noRows: "No contracts match the current filters.",
        error: "The table could not be read.",
        errTimeout: "The request took too long. The chain may still be building — try again in a moment.",
        errNoChain: "No chain is available for this underlying and expiry.",
        retry: "Try again",
        now: "Now",
        prior: "Prior",
        chg: "Chg",
        pct: "%",
        nowSession: "Now",
        priorSession: "vs prior close",
        snapshot: "snapshot",
        live: "live chain",
        spot: "Spot",
        matched: "with a prior close",
        contracts: "contracts",
        callsOi: "Calls · OI change",
        putsOi: "Puts · OI change",
        callsVol: "Calls · volume vs prior",
        putsVol: "Puts · volume vs prior",
        coverage: "Coverage",
        chartTitle: (m: string) => `${m} — prior close vs now, across strikes`,
        chartMetric: "Metric",
        chartExpiry: "Expiry",
        chartSide: "Side",
        chartEmpty: "Nothing to draw for this expiry and side.",
        strike: "Strike",
        contract: "Contract",
        noPrior: "No prior close (new contract)",
        oiNote: "OI is the figure published for each session (positions at the previous close). Before the open, “now” is still the last session.",
        rank: {
            oi: "|Δ OI|",
            volume: "|Δ Volume|",
            iv: "|Δ IV|",
            gamma: "|Δ Γ|",
            delta: "|Δ Δ|",
            strike: "Strike",
        } as Record<string, string>,
        metric: {
            oi: "OI",
            volume: "Volume",
            delta: "Δ Delta",
            gamma: "Γ Gamma",
            theta: "Θ Theta",
            vega: "ν Vega",
            iv: "IV",
            mid: "Mid",
        } as Record<ChangeMetric, string>,
        short: { oi: "OI", volume: "Vol", delta: "Δ", gamma: "Γ", theta: "Θ", vega: "ν", iv: "IV", mid: "Mid" } as Record<ChangeMetric, string>,
        bid: "Bid",
        ask: "Ask",
    },
    ar: {
        eyebrow: "الأدوات",
        title: "جدول تغيّر العقود — أبو سلطان 2.0",
        idea: "صف لكل عقد: الفائدة المفتوحة والحجم والإغريق (Δ Γ Θ ν) والتذبذب الضمني والسعر الأوسط الآن مقابل إغلاق الجلسة السابقة. اختر الأعمدة واستخدم الرسم أدناه (مثل Γ السابق مقابل الآن).",
        caption: "قراءة آلية للبيانات، وليست توصية",
        underlying: "الأصل",
        side: "النوع",
        all: "الكل",
        calls: "كول",
        puts: "بوت",
        expiries: `تواريخ الانتهاء (حتى ${MAX_EXPIRIES})`,
        window: "الأسعار",
        windowAll: "كل الأسعار",
        nearSpot: (p: number) => `±${p}% من السعر`,
        rankBy: "الترتيب حسب",
        rows: "الصفوف",
        columns: "الأعمدة",
        showPct: "إظهار %",
        quote: "عرض / طلب",
        refresh: "تحديث",
        loadingExp: "جارٍ قراءة تواريخ الانتهاء…",
        loading: "جارٍ قراءة السلسلة وإغلاق الجلسة السابقة…",
        pickExpiry: "اختر تاريخ انتهاء محدداً واحداً على الأقل أعلاه. يُقارَن كل تاريخ بإغلاق جلسته السابقة.",
        noExp: "لا توجد تواريخ انتهاء لهذا الأصل.",
        noRows: "لا توجد عقود تطابق المرشحات.",
        error: "تعذّرت قراءة الجدول.",
        errTimeout: "استغرق الطلب وقتاً طويلاً. قد تكون السلسلة قيد الإعداد — حاول بعد قليل.",
        errNoChain: "لا تتوفر سلسلة لهذا الأصل وتاريخ الانتهاء.",
        retry: "حاول مجدداً",
        now: "الآن",
        prior: "السابق",
        chg: "التغيّر",
        pct: "%",
        nowSession: "الآن",
        priorSession: "مقابل الإغلاق السابق",
        snapshot: "لقطة",
        live: "سلسلة مباشرة",
        spot: "السعر",
        matched: "لها إغلاق سابق",
        contracts: "عقد",
        callsOi: "كول · تغيّر الفائدة المفتوحة",
        putsOi: "بوت · تغيّر الفائدة المفتوحة",
        callsVol: "كول · الحجم مقابل السابق",
        putsVol: "بوت · الحجم مقابل السابق",
        coverage: "التغطية",
        chartTitle: (m: string) => `${m} — الإغلاق السابق مقابل الآن عبر الأسعار`,
        chartMetric: "المقياس",
        chartExpiry: "الانتهاء",
        chartSide: "النوع",
        chartEmpty: "لا يوجد ما يُرسم لهذا التاريخ والنوع.",
        strike: "السعر",
        contract: "العقد",
        noPrior: "لا إغلاق سابق (عقد جديد)",
        oiNote: "الفائدة المفتوحة هي الرقم المنشور لكل جلسة (المراكز عند الإغلاق السابق). قبل الافتتاح يبقى «الآن» هو الجلسة الأخيرة.",
        rank: {
            oi: "|Δ الفائدة المفتوحة|",
            volume: "|Δ الحجم|",
            iv: "|Δ التذبذب|",
            gamma: "|Δ Γ|",
            delta: "|Δ دلتا|",
            strike: "السعر",
        } as Record<string, string>,
        metric: {
            oi: "الفائدة المفتوحة",
            volume: "الحجم",
            delta: "Δ دلتا",
            gamma: "Γ جاما",
            theta: "Θ ثيتا",
            vega: "ν فيغا",
            iv: "التذبذب الضمني",
            mid: "السعر الأوسط",
        } as Record<ChangeMetric, string>,
        short: { oi: "OI", volume: "الحجم", delta: "Δ", gamma: "Γ", theta: "Θ", vega: "ν", iv: "IV", mid: "الأوسط" } as Record<ChangeMetric, string>,
        bid: "عرض",
        ask: "طلب",
    },
};

// ── Formatting ───────────────────────────────────────────────────────────────

const DIGITS: Record<ChangeMetric, number> = { oi: 0, volume: 0, delta: 3, gamma: 4, theta: 3, vega: 3, iv: 1, mid: 2 };

function scaled(m: ChangeMetric, v: Num): Num {
    if (v == null || !Number.isFinite(v)) return null;
    return m === "iv" ? v * 100 : v;
}

function fmt(m: ChangeMetric, v: Num, signed = false): string {
    const x = scaled(m, v);
    if (x == null) return "—";
    const d = DIGITS[m];
    const s = Math.abs(x).toLocaleString("en-US", { minimumFractionDigits: d, maximumFractionDigits: d });
    const sign = x < 0 ? "−" : signed && x > 0 ? "+" : "";
    return `${sign}${s}${m === "iv" ? "%" : ""}`;
}

function fmtPct(v: Num): string {
    if (v == null || !Number.isFinite(v)) return "—";
    if (Math.abs(v) >= 10000) return v > 0 ? "+9999%" : "−9999%";
    return `${v > 0 ? "+" : v < 0 ? "−" : ""}${Math.abs(v).toFixed(1)}%`;
}

function fmtQuote(v: Num): string {
    return v == null || !Number.isFinite(v) ? "—" : v.toFixed(2);
}

function fmtCompact(v: Num, signed = false): string {
    if (v == null || !Number.isFinite(v)) return "—";
    const a = Math.abs(v);
    const body =
        a >= 1e6 ? `${(a / 1e6).toFixed(2)}M` : a >= 1e4 ? `${(a / 1e3).toFixed(1)}K` : Math.round(a).toLocaleString("en-US");
    return `${v < 0 ? "−" : signed && v > 0 ? "+" : ""}${body}`;
}

function tone(v: Num): string {
    if (v == null || !Number.isFinite(v) || v === 0) return "";
    return v > 0 ? "pos" : "neg";
}

function shortDate(iso: string, lang: Lang): string {
    const d = new Date(`${iso}T12:00:00Z`);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString(lang === "ar" ? "ar-EG" : "en-US", { day: "numeric", month: "short", timeZone: "UTC" });
}

function dteOf(iso: string): number | null {
    const t = Date.parse(`${iso}T21:00:00Z`);
    if (!Number.isFinite(t)) return null;
    return Math.max(0, Math.round((t - Date.now()) / 86400000));
}

function timeEt(ts: string): string {
    const d = new Date(ts);
    if (Number.isNaN(d.getTime())) return "";
    return `${d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: false, timeZone: "America/New_York" })} ET`;
}

// ── Per-viewer conveniences (never required) ─────────────────────────────────

const LS_KEY = "mmec.contractChanges.v1";

interface Saved {
    symbol?: string;
    expiries?: Record<string, string[]>;
    cols?: ChangeMetric[];
    showPct?: boolean;
    showQuote?: boolean;
    windowPct?: number;
    chartMetric?: ChangeMetric;
}

function readSaved(): Saved {
    try {
        const raw = window.localStorage.getItem(LS_KEY);
        return raw ? (JSON.parse(raw) as Saved) : {};
    } catch {
        return {};
    }
}

function writeSaved(patch: Saved): void {
    try {
        const cur = readSaved();
        window.localStorage.setItem(LS_KEY, JSON.stringify({ ...cur, ...patch }));
    } catch {
        /* storage unavailable */
    }
}

// ── Component ────────────────────────────────────────────────────────────────

type Side = "all" | "C" | "P";
type RankKey = "oi" | "volume" | "iv" | "gamma" | "delta" | "strike";
type SortField = "now" | "prior" | "chg" | "pct";
interface SortState {
    key: ChangeMetric | "strike" | "contract";
    field: SortField;
    dir: "asc" | "desc";
}

const DEFAULT_COLS: ChangeMetric[] = ["oi", "volume", "delta", "gamma", "theta", "vega", "iv"];
const WINDOWS = [5, 10, 20, 0];
const ROW_LIMITS = [50, 100, 250, 0];
const REFRESH_MS = 60_000;

export interface ContractChangesViewProps {
    /** Underlyings to suggest (from the app's loaded chain); any symbol can be typed. */
    suggestions?: string[];
}

const ContractChangesView: React.FC<ContractChangesViewProps> = ({ suggestions }) => {
    const language = useContext(LanguageContext)?.language ?? "en";
    const lang: Lang = language === "ar" ? "ar" : "en";
    const ds = isDesignSystemOn();
    const c = COPY[lang];
    const ct = useChartTheme();
    const saved = useMemo(readSaved, []);

    const [symbolInput, setSymbolInput] = useState(saved.symbol || "SPY");
    const [symbol, setSymbol] = useState(normaliseSymbol(saved.symbol || "SPY") || "SPY");
    const [expList, setExpList] = useState<string[]>([]);
    const [expLoading, setExpLoading] = useState(false);
    const [expError, setExpError] = useState<string | null>(null);
    const [selected, setSelected] = useState<string[]>([]);
    const [side, setSide] = useState<Side>("all");
    const [cols, setCols] = useState<ChangeMetric[]>(
        saved.cols && saved.cols.length ? saved.cols.filter((m) => CHANGE_METRICS.includes(m)) : DEFAULT_COLS,
    );
    const [showPct, setShowPct] = useState(!!saved.showPct);
    const [showQuote, setShowQuote] = useState(saved.showQuote !== false);
    const [windowPct, setWindowPct] = useState<number>(saved.windowPct ?? 10);
    const [rank, setRank] = useState<RankKey>("oi");
    const [sort, setSort] = useState<SortState | null>(null);
    const [rowLimit, setRowLimit] = useState(100);
    const [data, setData] = useState<ContractChangesResponse | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [chartMetric, setChartMetric] = useState<ChangeMetric>(saved.chartMetric || "gamma");
    const [chartSide, setChartSide] = useState<"C" | "P">("C");
    const [chartExpiry, setChartExpiry] = useState<string>("");
    const [reloadTick, setReloadTick] = useState(0);
    const reqRef = useRef(0);

    const suggestList = useMemo(() => {
        const base = ["SPY", "QQQ", "SPX", "IWM", "NDX", "AAPL", "TSLA", "NVDA"];
        const set = new Set<string>(base);
        (suggestions || []).forEach((s) => {
            const n = normaliseSymbol(s);
            if (n) set.add(n);
        });
        return Array.from(set).slice(0, 60);
    }, [suggestions]);

    // Expiries for the symbol.
    useEffect(() => {
        const ac = new AbortController();
        setExpLoading(true);
        setExpError(null);
        setExpList([]);
        setSelected([]);
        setData(null);
        fetchChangeExpiries(symbol, ac.signal)
            .then((r) => {
                if (ac.signal.aborted) return;
                setExpList(r.expiries || []);
                const remembered = (readSaved().expiries || {})[symbol] || [];
                const keep = remembered.filter((e) => r.expiries.includes(e)).slice(0, MAX_EXPIRIES);
                setSelected(keep.length ? keep : r.expiries.slice(0, 1));
            })
            .catch((e) => {
                if (ac.signal.aborted) return;
                setExpError(e instanceof ContractChangesError ? e.code : String(e));
            })
            .finally(() => {
                if (!ac.signal.aborted) setExpLoading(false);
            });
        writeSaved({ symbol });
        return () => ac.abort();
    }, [symbol]);

    const selKey = useMemo(() => [...selected].sort().join(","), [selected]);

    // The table itself.
    const load = useCallback(
        (signal: AbortSignal, quiet: boolean) => {
            if (!selKey) {
                setData(null);
                return;
            }
            const id = ++reqRef.current;
            if (!quiet) setLoading(true);
            setError(null);
            fetchContractChanges(symbol, selKey.split(","), signal)
                .then((r) => {
                    if (signal.aborted || id !== reqRef.current) return;
                    setData(r);
                })
                .catch((e) => {
                    if (signal.aborted || id !== reqRef.current) return;
                    if (!quiet) setData(null);
                    setError(e instanceof ContractChangesError ? e.code : String(e));
                })
                .finally(() => {
                    if (!signal.aborted && id === reqRef.current) setLoading(false);
                });
        },
        [symbol, selKey],
    );

    useEffect(() => {
        const ac = new AbortController();
        load(ac.signal, false);
        const timer = window.setInterval(() => {
            if (document.visibilityState === "visible") load(ac.signal, true);
        }, REFRESH_MS);
        return () => {
            ac.abort();
            window.clearInterval(timer);
        };
    }, [load, reloadTick]);

    useEffect(() => {
        if (!selKey) return;
        const all = readSaved().expiries || {};
        writeSaved({ expiries: { ...all, [symbol]: selKey.split(",") } });
    }, [selKey, symbol]);

    useEffect(() => {
        writeSaved({ cols, showPct, showQuote, windowPct, chartMetric });
    }, [cols, showPct, showQuote, windowPct, chartMetric]);

    useEffect(() => {
        const list = data?.expiries || [];
        if (!list.includes(chartExpiry)) setChartExpiry(list[0] || "");
    }, [data, chartExpiry]);

    const toggleExpiry = (e: string) => {
        setSelected((cur) => {
            if (cur.includes(e)) return cur.filter((x) => x !== e);
            if (cur.length >= MAX_EXPIRIES) return cur;
            return [...cur, e];
        });
    };

    const toggleCol = (m: ChangeMetric) => {
        setCols((cur) => (cur.includes(m) ? cur.filter((x) => x !== m) : CHANGE_METRICS.filter((x) => x === m || cur.includes(x))));
    };

    const submitSymbol = () => {
        const n = normaliseSymbol(symbolInput);
        if (n && n !== symbol) setSymbol(n);
        setSymbolInput(n || symbol);
    };

    const spotNow = data?.spot?.now ?? null;

    const filtered = useMemo(() => {
        const rows = data?.rows || [];
        return rows.filter((r) => {
            if (side !== "all" && r.right !== side) return false;
            if (windowPct > 0 && spotNow && spotNow > 0) {
                if (Math.abs(r.strike - spotNow) / spotNow > windowPct / 100) return false;
            }
            return true;
        });
    }, [data, side, windowPct, spotNow]);

    const sorted = useMemo(() => {
        const out = [...filtered];
        const val = (r: ContractChangeRow, s: SortState): number | string | null => {
            if (s.key === "strike") return r.strike;
            if (s.key === "contract") return r.contract;
            return r[s.key][s.field];
        };
        if (sort) {
            out.sort((a, b) => {
                const va = val(a, sort);
                const vb = val(b, sort);
                if (va == null && vb == null) return 0;
                if (va == null) return 1;
                if (vb == null) return -1;
                const cmp = typeof va === "string" ? va.localeCompare(String(vb)) : (va as number) - (vb as number);
                return sort.dir === "asc" ? cmp : -cmp;
            });
        } else if (rank === "strike") {
            out.sort((a, b) => a.expiry.localeCompare(b.expiry) || a.strike - b.strike || a.right.localeCompare(b.right));
        } else {
            const mag = (r: ContractChangeRow) => {
                const v = r[rank].chg;
                return v == null ? -1 : Math.abs(v);
            };
            out.sort((a, b) => mag(b) - mag(a) || a.strike - b.strike);
        }
        return rowLimit > 0 ? out.slice(0, rowLimit) : out;
    }, [filtered, sort, rank, rowLimit]);

    const clickSort = (key: SortState["key"], field: SortField = "chg") => {
        setSort((cur) =>
            cur && cur.key === key && cur.field === field
                ? cur.dir === "desc"
                    ? { key, field, dir: "asc" }
                    : null
                : { key, field, dir: "desc" },
        );
    };

    const sortMark = (key: SortState["key"], field: SortField = "chg") =>
        sort && sort.key === key && sort.field === field ? (
            <span className="edge-sort">{sort.dir === "desc" ? "▼" : "▲"}</span>
        ) : null;

    const chartData = useMemo(() => {
        const rows = (data?.rows || []).filter((r) => r.expiry === chartExpiry && r.right === chartSide);
        return rows
            .filter((r) => {
                if (windowPct > 0 && spotNow && spotNow > 0) return Math.abs(r.strike - spotNow) / spotNow <= windowPct / 100;
                return true;
            })
            .sort((a, b) => a.strike - b.strike)
            .map((r) => ({
                strike: r.strike,
                prior: scaled(chartMetric, r[chartMetric].prior),
                now: scaled(chartMetric, r[chartMetric].now),
                chg: scaled(chartMetric, r[chartMetric].chg),
            }));
    }, [data, chartExpiry, chartSide, chartMetric, windowPct, spotNow]);

    const errText = (code: string | null) =>
        code === "timeout" ? c.errTimeout : code === "no_chain" ? c.errNoChain : c.error;

    const fieldList: SortField[] = showPct ? ["now", "prior", "chg", "pct"] : ["now", "prior", "chg"];
    const fieldLabel: Record<SortField, string> = { now: c.now, prior: c.prior, chg: c.chg, pct: c.pct };

    const sum = data?.summary;
    const tile = (k: string, chg: Num, pct: Num, sub?: string) => (
        <div className="cc-tile">
            <div className="cc-tile-k">{k}</div>
            <div className="cc-tile-v edge-num">
                {fmtCompact(chg, true)}
                <span className="cc-tile-s" style={{ marginInlineStart: 6 }}>{fmtPct(pct)}</span>
            </div>
            {sub ? <div className="cc-tile-s edge-num">{sub}</div> : null}
        </div>
    );

    const sourcesLive = data ? Object.values(data.sources || {}).some((s) => s === "live") : false;

    return (
        <div className="edge-root" dir={lang === "ar" ? "rtl" : "ltr"}>
            <header className="edge-head">
                <div>
                    <div className="edge-eyebrow">{c.eyebrow}</div>
                    <h1 className="edge-title">{c.title}</h1>
                    <p className="edge-idea">{c.idea}</p>
                </div>
                <div className="edge-head-right">
                    {data ? (
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, justifyContent: "flex-end" }}>
                            <span className="edge-chip">
                                <i />
                                {c.nowSession}: <span className="edge-num">{shortDate(data.now_session, lang)}</span>
                                <span className="edge-num" style={{ opacity: 0.75 }}>
                                    · {sourcesLive ? c.live : `${c.snapshot} ${timeEt(data.snapshot_ts)}`}
                                </span>
                            </span>
                            <span className="edge-chip is-off">
                                <i />
                                {c.priorSession}: <span className="edge-num">{shortDate(data.prior_session, lang)}</span>
                            </span>
                            <span className="edge-chip is-off">
                                {c.spot} <span className="edge-num">{data.spot.now?.toFixed(2) ?? "—"}</span>
                                <span className="edge-num" style={{ opacity: 0.7 }}>
                                    ({data.spot.prior?.toFixed(2) ?? "—"})
                                </span>
                            </span>
                        </div>
                    ) : null}
                    <span className="edge-caption">{c.caption}</span>
                </div>
            </header>

            <section className="edge-filters" aria-label={c.underlying}>
                <div className="edge-field">
                    <label htmlFor="cc-sym">{c.underlying}</label>
                    <form
                        onSubmit={(e) => {
                            e.preventDefault();
                            submitSymbol();
                        }}
                    >
                        <input
                            id="cc-sym"
                            className="cc-sym"
                            list="cc-sym-list"
                            value={symbolInput}
                            onChange={(e) => setSymbolInput(e.target.value.toUpperCase())}
                            onBlur={submitSymbol}
                            autoComplete="off"
                            spellCheck={false}
                        />
                        <datalist id="cc-sym-list">
                            {suggestList.map((s) => (
                                <option key={s} value={s} />
                            ))}
                        </datalist>
                    </form>
                </div>
                <div className="edge-field">
                    <label>{c.side}</label>
                    <div className="cc-seg" role="group" aria-label={c.side}>
                        {(["all", "C", "P"] as Side[]).map((s) => (
                            <button key={s} type="button" className={side === s ? "is-on" : ""} onClick={() => setSide(s)}>
                                {s === "all" ? c.all : s === "C" ? c.calls : c.puts}
                            </button>
                        ))}
                    </div>
                </div>
                <div className="edge-field">
                    <label htmlFor="cc-win">{c.window}</label>
                    <select id="cc-win" className="cc-select" value={windowPct} onChange={(e) => setWindowPct(Number(e.target.value))}>
                        {WINDOWS.map((w) => (
                            <option key={w} value={w}>
                                {w === 0 ? c.windowAll : c.nearSpot(w)}
                            </option>
                        ))}
                    </select>
                </div>
                <div className="edge-field">
                    <label htmlFor="cc-rank">{c.rankBy}</label>
                    <select
                        id="cc-rank"
                        className="cc-select"
                        value={sort ? "" : rank}
                        onChange={(e) => {
                            setSort(null);
                            setRank(e.target.value as RankKey);
                        }}
                    >
                        {sort ? <option value="">—</option> : null}
                        {(["oi", "volume", "iv", "gamma", "delta", "strike"] as RankKey[]).map((k) => (
                            <option key={k} value={k}>
                                {c.rank[k]}
                            </option>
                        ))}
                    </select>
                </div>
                <div className="edge-field">
                    <label htmlFor="cc-rows">{c.rows}</label>
                    <select id="cc-rows" className="cc-select" value={rowLimit} onChange={(e) => setRowLimit(Number(e.target.value))}>
                        {ROW_LIMITS.map((n) => (
                            <option key={n} value={n}>
                                {n === 0 ? c.all : n}
                            </option>
                        ))}
                    </select>
                </div>
                <div className="edge-filters-right">
                    <button type="button" className="edge-btn" onClick={() => setReloadTick((t) => t + 1)} disabled={loading || !selKey}>
                        {c.refresh}
                    </button>
                </div>

                <div className="edge-field cc-field-wide" style={{ flexBasis: "100%" }}>
                    <label>{c.expiries}</label>
                    {expLoading ? (
                        <span className="edge-caption">{c.loadingExp}</span>
                    ) : expError ? (
                        <span className="edge-caption" style={{ color: "var(--e-bad)" }}>
                            {errText(expError)}
                        </span>
                    ) : expList.length === 0 ? (
                        <span className="edge-caption">{c.noExp}</span>
                    ) : (
                        <div className="cc-chips">
                            {expList.map((e) => {
                                const on = selected.includes(e);
                                const dte = dteOf(e);
                                return (
                                    <button
                                        key={e}
                                        type="button"
                                        className={`cc-chip${on ? " is-on" : ""}`}
                                        aria-pressed={on}
                                        disabled={!on && selected.length >= MAX_EXPIRIES}
                                        onClick={() => toggleExpiry(e)}
                                    >
                                        {e}
                                        {dte != null ? <span className="cc-dte">{dte}d</span> : null}
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </div>

                <div className="edge-field cc-field-wide" style={{ flexBasis: "100%" }}>
                    <label>{c.columns}</label>
                    <div className="cc-chips">
                        {CHANGE_METRICS.map((m) => (
                            <button
                                key={m}
                                type="button"
                                className={`cc-chip is-ui${cols.includes(m) ? " is-on" : ""}`}
                                aria-pressed={cols.includes(m)}
                                onClick={() => toggleCol(m)}
                            >
                                {c.metric[m]}
                            </button>
                        ))}
                        <button
                            type="button"
                            className={`cc-chip is-ui${showQuote ? " is-on" : ""}`}
                            aria-pressed={showQuote}
                            onClick={() => setShowQuote((v) => !v)}
                        >
                            <span className="cc-bid">{c.bid}</span> / <span className="cc-ask">{c.ask}</span>
                        </button>
                        <button
                            type="button"
                            className={`cc-chip is-ui${showPct ? " is-on" : ""}`}
                            aria-pressed={showPct}
                            onClick={() => setShowPct((v) => !v)}
                        >
                            {c.showPct}
                        </button>
                    </div>
                </div>
            </section>

            {!selKey && !expLoading && expList.length > 0 ? (
                ds ? <ToolState kind="empty" lang={lang} title={c.pickExpiry} body="" /> : <div className="edge-state">{c.pickExpiry}</div>
            ) : loading && !data ? (
                ds ? <ToolState kind="loading" lang={lang} /> : <div className="edge-state">{c.loading}</div>
            ) : error && !data && ds ? (
                <ToolState kind="error" lang={lang} title={errText(error)} body="" actionLabel={c.retry} onAction={() => setReloadTick((t) => t + 1)} />
            ) : error && !data ? (
                <div className="edge-state is-error">
                    {errText(error)}{" "}
                    <button type="button" className="edge-btn" onClick={() => setReloadTick((t) => t + 1)}>
                        {c.retry}
                    </button>
                </div>
            ) : data ? (
                <>
                    {sum ? (
                        <div className="cc-tiles">
                            {tile(c.callsOi, sum.calls.oi.chg, sum.calls.oi.pct, `${fmtCompact(sum.calls.oi.prior)} → ${fmtCompact(sum.calls.oi.now)}`)}
                            {tile(c.putsOi, sum.puts.oi.chg, sum.puts.oi.pct, `${fmtCompact(sum.puts.oi.prior)} → ${fmtCompact(sum.puts.oi.now)}`)}
                            {tile(c.callsVol, sum.calls.volume.chg, sum.calls.volume.pct, `${fmtCompact(sum.calls.volume.prior)} → ${fmtCompact(sum.calls.volume.now)}`)}
                            {tile(c.putsVol, sum.puts.volume.chg, sum.puts.volume.pct, `${fmtCompact(sum.puts.volume.prior)} → ${fmtCompact(sum.puts.volume.now)}`)}
                            <div className="cc-tile">
                                <div className="cc-tile-k">{c.coverage}</div>
                                <div className="cc-tile-v edge-num">
                                    {sum.matched.toLocaleString("en-US")} / {sum.contracts.toLocaleString("en-US")}
                                </div>
                                <div className="cc-tile-s">
                                    {c.contracts} {c.matched}
                                </div>
                            </div>
                        </div>
                    ) : null}

                    {sorted.length === 0 ? (
                        ds ? <ToolState kind="empty" lang={lang} title={c.noRows} body="" /> : <div className="edge-state">{c.noRows}</div>
                    ) : (
                        <div className="edge-tablewrap cc-tablewrap">
                            <table className="edge-table cc-table">
                                <thead>
                                    <tr className="cc-group">
                                        <th colSpan={1} />
                                        {showQuote ? (
                                            <th colSpan={2} className="cc-gstart">
                                                <span className="cc-bid">{c.bid}</span> / <span className="cc-ask">{c.ask}</span>
                                            </th>
                                        ) : null}
                                        {cols.map((m) => (
                                            <th key={m} colSpan={fieldList.length} className="cc-gstart">
                                                {c.metric[m]}
                                            </th>
                                        ))}
                                    </tr>
                                    <tr className="cc-sub">
                                        <th
                                            className={sort?.key === "strike" ? "is-sorted" : ""}
                                            onClick={() => clickSort("strike", "now")}
                                        >
                                            {c.contract}
                                            {sortMark("strike", "now")}
                                        </th>
                                        {showQuote ? (
                                            <>
                                                <th className="num cc-gstart cc-bid" style={{ cursor: "default" }}>
                                                    {c.bid}
                                                </th>
                                                <th className="num cc-ask" style={{ cursor: "default" }}>
                                                    {c.ask}
                                                </th>
                                            </>
                                        ) : null}
                                        {cols.map((m) =>
                                            fieldList.map((f, i) => (
                                                <th
                                                    key={`${m}-${f}`}
                                                    className={`num${i === 0 ? " cc-gstart" : ""}${
                                                        sort?.key === m && sort.field === f ? " is-sorted" : ""
                                                    }`}
                                                    onClick={() => clickSort(m, f)}
                                                    title={`${c.metric[m]} · ${fieldLabel[f]}`}
                                                >
                                                    {fieldLabel[f]}
                                                    {sortMark(m, f)}
                                                </th>
                                            )),
                                        )}
                                    </tr>
                                </thead>
                                <tbody>
                                    {sorted.map((r) => (
                                        <tr
                                            key={`${r.expiry}|${r.strike}|${r.right}`}
                                            className={r.has_prior ? "" : "is-noprior"}
                                            title={r.has_prior ? undefined : c.noPrior}
                                        >
                                            <td className="cc-c edge-num">
                                                {r.strike.toLocaleString("en-US", { maximumFractionDigits: 3 })}
                                                <span className="cc-right">{r.right}</span>
                                                <span className="edge-sub">{r.expiry}</span>
                                            </td>
                                            {showQuote ? (
                                                <>
                                                    <td className="num edge-num bid cc-gstart">{fmtQuote(r.bid)}</td>
                                                    <td className="num edge-num ask">{fmtQuote(r.ask)}</td>
                                                </>
                                            ) : null}
                                            {cols.map((m) =>
                                                fieldList.map((f, i) => {
                                                    const p = r[m];
                                                    const v = p[f];
                                                    const cls = [
                                                        "num",
                                                        "edge-num",
                                                        i === 0 ? "cc-gstart" : "",
                                                        v == null ? "is-null" : "",
                                                        f === "chg" || f === "pct" ? tone(v) : "",
                                                    ]
                                                        .filter(Boolean)
                                                        .join(" ");
                                                    return (
                                                        <td key={`${m}-${f}`} className={cls}>
                                                            {f === "pct" ? fmtPct(v) : fmt(m, v, f === "chg")}
                                                        </td>
                                                    );
                                                }),
                                            )}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    <section className="cc-chartcard" aria-label={c.chartTitle(c.metric[chartMetric])}>
                        <div className="cc-chart-head">
                            <div>
                                <div className="cc-chart-title">{c.chartTitle(c.metric[chartMetric])}</div>
                                <div className="cc-chart-sub edge-num">
                                    {data.symbol} · {chartExpiry} · {chartSide === "C" ? c.calls : c.puts} ·{" "}
                                    {shortDate(data.prior_session, lang)} → {shortDate(data.now_session, lang)}
                                </div>
                            </div>
                            <div className="cc-chart-ctl">
                                <select
                                    className="cc-select"
                                    aria-label={c.chartMetric}
                                    value={chartMetric}
                                    onChange={(e) => setChartMetric(e.target.value as ChangeMetric)}
                                >
                                    {CHANGE_METRICS.map((m) => (
                                        <option key={m} value={m}>
                                            {c.metric[m]}
                                        </option>
                                    ))}
                                </select>
                                <select
                                    className="cc-select"
                                    aria-label={c.chartExpiry}
                                    value={chartExpiry}
                                    onChange={(e) => setChartExpiry(e.target.value)}
                                >
                                    {(data.expiries || []).map((e) => (
                                        <option key={e} value={e}>
                                            {e}
                                        </option>
                                    ))}
                                </select>
                                <div className="cc-seg" role="group" aria-label={c.chartSide}>
                                    {(["C", "P"] as const).map((s) => (
                                        <button
                                            key={s}
                                            type="button"
                                            className={chartSide === s ? "is-on" : ""}
                                            onClick={() => setChartSide(s)}
                                        >
                                            {s === "C" ? c.calls : c.puts}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                        {chartData.length < 2 ? (
                            <div className="edge-state">{c.chartEmpty}</div>
                        ) : (
                            <div style={{ width: "100%", height: 300, direction: "ltr" }}>
                                <ResponsiveContainer width="100%" height="100%">
                                    <LineChart data={chartData} margin={{ top: 8, right: 16, bottom: 4, left: 4 }}>
                                        <CartesianGrid stroke={ct.grid} strokeDasharray="2 4" vertical={false} />
                                        <XAxis
                                            dataKey="strike"
                                            type="number"
                                            domain={["dataMin", "dataMax"]}
                                            tick={{ fill: ct.text, fontSize: 11 }}
                                            tickLine={false}
                                            axisLine={{ stroke: ct.border }}
                                        />
                                        <YAxis
                                            width={64}
                                            tick={{ fill: ct.text, fontSize: 11 }}
                                            tickLine={false}
                                            axisLine={false}
                                            tickFormatter={(v: number) =>
                                                chartMetric === "oi" || chartMetric === "volume"
                                                    ? fmtCompact(v)
                                                    : `${Number(v).toFixed(DIGITS[chartMetric])}${chartMetric === "iv" ? "%" : ""}`
                                            }
                                        />
                                        {spotNow ? (
                                            <ReferenceLine
                                                x={spotNow}
                                                stroke={ct.muted}
                                                strokeDasharray="3 3"
                                                label={{ value: `${c.spot} ${spotNow.toFixed(2)}`, fill: ct.text, fontSize: 10, position: "insideTopRight" }}
                                            />
                                        ) : null}
                                        <Tooltip
                                            cursor={{ stroke: ct.border }}
                                            content={({ active, payload, label }: any) => {
                                                if (!active || !payload || !payload.length) return null;
                                                const p = payload[0].payload as { prior: Num; now: Num; chg: Num };
                                                const f = (v: Num) =>
                                                    v == null
                                                        ? "—"
                                                        : `${v.toLocaleString("en-US", {
                                                              maximumFractionDigits: DIGITS[chartMetric],
                                                              minimumFractionDigits: DIGITS[chartMetric],
                                                          })}${chartMetric === "iv" ? "%" : ""}`;
                                                return (
                                                    <div className="cc-tip edge-num">
                                                        <div>
                                                            <b>
                                                                {c.strike} {label}
                                                                {chartSide}
                                                            </b>
                                                        </div>
                                                        <div className="cc-tip-row">
                                                            <span>{c.prior}</span>
                                                            <span>{f(p.prior)}</span>
                                                        </div>
                                                        <div className="cc-tip-row">
                                                            <span>{c.now}</span>
                                                            <span>{f(p.now)}</span>
                                                        </div>
                                                        <div className="cc-tip-row">
                                                            <span>{c.chg}</span>
                                                            <span className={tone(p.chg) === "pos" ? "cc-ask" : tone(p.chg) === "neg" ? "cc-bid" : ""}>
                                                                {p.chg == null ? "—" : `${p.chg > 0 ? "+" : ""}${f(p.chg)}`}
                                                            </span>
                                                        </div>
                                                    </div>
                                                );
                                            }}
                                        />
                                        <Legend wrapperStyle={{ fontSize: 11, color: ct.text }} iconType="plainline" />
                                        <Line
                                            name={`${c.prior} (${shortDate(data.prior_session, lang)})`}
                                            type="monotone"
                                            dataKey="prior"
                                            stroke={ct.muted}
                                            strokeWidth={2}
                                            strokeDasharray="5 4"
                                            dot={false}
                                            activeDot={{ r: 4 }}
                                            connectNulls
                                            isAnimationActive={false}
                                        />
                                        <Line
                                            name={`${c.now} (${shortDate(data.now_session, lang)})`}
                                            type="monotone"
                                            dataKey="now"
                                            stroke={ct.accent}
                                            strokeWidth={2}
                                            dot={false}
                                            activeDot={{ r: 4 }}
                                            connectNulls
                                            isAnimationActive={false}
                                        />
                                    </LineChart>
                                </ResponsiveContainer>
                            </div>
                        )}
                    </section>

                    <footer className="edge-foot">
                        <span>{c.oiNote}</span>
                        {error ? <span style={{ color: "var(--e-bad)" }}>{errText(error)}</span> : null}
                        {loading ? <span>{c.loading}</span> : null}
                    </footer>
                </>
            ) : null}
        </div>
    );
};

export default ContractChangesView;
